/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { OverrideSource } from './models';

export interface ConfigOverride<TValue = unknown, TPath extends string = string> {
  value: TValue;
  path?: TPath;
  source?: OverrideSource;
  required?: boolean;
}

export type ConfigOverrideMap<T extends object, TPath extends string = string> = Partial<{
  [Key in keyof T & string]: ConfigOverride<T[Key], TPath>;
}>;

export const createConfigOverrideMap = <T extends object, TPath extends string = string>(
  overrides: T | undefined,
  options?: {
    paths?: Partial<Record<keyof T & string, TPath>>;
    sources?: Partial<Record<keyof T & string, OverrideSource>>;
    requiredPaths?: Set<string>;
  },
): ConfigOverrideMap<T, TPath> | undefined => {
  if (!overrides) return undefined;

  return Object.entries(overrides).reduce<ConfigOverrideMap<T, TPath>>((map, [key, value]) => {
    const pathKey = key as keyof T & string;

    map[pathKey] = {
      value: value as T[typeof pathKey],
      path: options?.paths?.[pathKey],
      source: options?.sources?.[pathKey],
      required: options?.requiredPaths?.has(pathKey),
    };
    return map;
  }, {});
};

export const toRawOverrides = <T extends object>(
  overrideMap: ConfigOverrideMap<T> | undefined,
): Partial<T> | undefined => {
  if (!overrideMap) return undefined;

  return Object.entries(overrideMap as Record<string, ConfigOverride | undefined>).reduce<
    Partial<T>
  >((rawOverrides, [key, entry]) => {
    if (!entry) return rawOverrides;
    rawOverrides[key as keyof T] = entry.value as T[keyof T];
    return rawOverrides;
  }, {});
};

export const getOverrideForPath = <T extends object>(
  overrideMap: ConfigOverrideMap<T> | undefined,
  pathKey: string,
  topKey: string | undefined,
): ConfigOverride | undefined => {
  if (!overrideMap) return undefined;
  const entries = overrideMap as Record<string, ConfigOverride | undefined>;
  return entries[pathKey] ?? (topKey ? entries[topKey] : undefined);
};
